import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import {
  Enum_Mobile_Permission_Status_Enum,
  useGetClientMobileDeviceDetailsQuery,
} from "../generated/graphql"
import { useApi } from "../utils/API"
import { useGraphQLClient } from "./useGraphQLClient"

// The mobile device details for a client
type MobileDeviceDetails = {
  gps: Enum_Mobile_Permission_Status_Enum
  motion: Enum_Mobile_Permission_Status_Enum
  notifications: Enum_Mobile_Permission_Status_Enum
  keyboard: Enum_Mobile_Permission_Status_Enum
  checkinTime: string
  os: string
  appVersion: string
  deviceModel: string
  updateAvailable: boolean
}

// A hook that fetches the mobile device details for a client
export default function useClientMobileDeviceDetails(clientId: string) {
  const graphqlClient = useGraphQLClient()
  const { data: rawDeviceDetails, error } =
    useGetClientMobileDeviceDetailsQuery(graphqlClient, {
      user_id: clientId,
    })
  const { getMostRecentAppVersions } = useApi()
  const [mostRecentAppVersions, setMostRecentAppVersions] = useState({
    apple: "",
    android: "",
  })
  const [fetchError, setFetchError] = useState<string | undefined>(undefined)

  // Updates the most recent versions when we pull the data
  useEffect(() => {
    getMostRecentAppVersions().then((response) => {
      setMostRecentAppVersions(response.data)
    })
  }, [getMostRecentAppVersions, rawDeviceDetails, clientId])

  // Set the error message if there is an error
  useEffect(() => {
    if (error) {
      // since error is unknown, we can't be sure it's a string so I'm just going to set it to a generic error message for
      setFetchError(
        "An error occurred while fetching the mobile device details."
      )
    }
    return undefined
  }, [error])

  // Checks if the given version is the most recent for the given OS
  function checkIfUpdateIsAvailable(version: string, os: string): boolean {
    if (os.toLowerCase().includes("ios")) {
      return !version.startsWith(mostRecentAppVersions.apple)
    }
    if (os.toLowerCase().includes("android")) {
      return !version.endsWith(mostRecentAppVersions.android)
    }
    return false
  }

  // The device details for the client
  const deviceDetails: MobileDeviceDetails = useMemo(() => {
    // Set defaults
    let permissions = {
      gps: Enum_Mobile_Permission_Status_Enum.Undetermined,
      motion: Enum_Mobile_Permission_Status_Enum.Undetermined,
      notifications: Enum_Mobile_Permission_Status_Enum.Undetermined,
      keyboard: Enum_Mobile_Permission_Status_Enum.Undetermined,
      checkinTime:
        rawDeviceDetails?.mobile_device_data[0]?.user.notification_time ?? "",
      os: rawDeviceDetails?.mobile_device_data[0]?.os_version ?? "",
      appVersion: rawDeviceDetails?.mobile_device_data[0]?.app_version ?? "",
      deviceModel: rawDeviceDetails?.mobile_device_data[0]?.device_model ?? "",
      updateAvailable: checkIfUpdateIsAvailable(
        rawDeviceDetails?.mobile_device_data[0]?.app_version ?? "",
        rawDeviceDetails?.mobile_device_data[0]?.os_version ?? ""
      ),
    }
    // Format the checkin time
    if (permissions.checkinTime.length > 0) {
      permissions.checkinTime = dayjs(permissions.checkinTime).format("h:mm A")
    }
    // fix the os version capitalization
    permissions.os = permissions.os.replace("ios", "iOS")
    permissions.os = permissions.os.replace("android", "Android")
    // If there are no permissions, return the defaults
    if (
      rawDeviceDetails === undefined ||
      rawDeviceDetails.mobile_device_data === undefined ||
      rawDeviceDetails.mobile_device_data.length === 0 ||
      rawDeviceDetails.mobile_device_data[0].devicePermissionsByUser.length ===
        0
    ) {
      return permissions
    }
    // Sort permissions by created_at with the most recent last so the switch statement sets the most recent permission
    const sortedPermissions =
      rawDeviceDetails?.mobile_device_data[0].devicePermissionsByUser.sort(
        (a, b) => (a.created_at < b.created_at ? -1 : 1)
      ) ?? []
    // Set the permission values
    for (const permission of sortedPermissions) {
      switch (permission.permission_type) {
        case "gps":
          permissions.gps = permission.permission_status
          break
        case "motion":
          permissions.motion = permission.permission_status
          break
        case "notification":
          permissions.notifications = permission.permission_status
          break
        case "keyboard":
          permissions.keyboard = permission.permission_status
          break
        default:
          break
      }
    }
    return permissions
  }, [rawDeviceDetails, checkIfUpdateIsAvailable, mostRecentAppVersions]) // mostRecentAppVersions is necessary for the useMemo to work properly

  return { permissions: deviceDetails, fetchError }
}
